import { useCallback, useContext, useEffect, useState } from 'react';
import isMobile from 'ismobilejs';
import styled from '@emotion/styled';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Skeleton from '@mui/material/Skeleton';
import type { WS } from '@trinity/chat';
import { ChatQueryStatus, QuerySocketEvent } from '@trinity/chat';
import { useAvatarPanel } from '@trinity/chat-components/components/DigitalTwin/AvatarPanel.context';
import { QuerySocketContext } from './context';
import { SystemAuthor } from './Message.Author';
import { SystemUserPlaceholderMessage } from './Message.System.Placeholder';
import { usePlaceholderStyles } from './Message.System.styles';

type Props = {
  status: ChatQueryStatus;
};

export const useChainOfThoughtSteps = () => {
  const [steps, setSteps] = useState<WS.QueryChainOfThought.Data[]>([]);
  const socket = useContext(QuerySocketContext);

  const updateSteps = useCallback((data: WS.QueryChainOfThought.Data) => {
    setSteps((prev) => {
      const existing = prev.find((x) => x.type === data.type);

      if (existing && data.status === 'in-progress') return prev;

      return data.status === 'finished'
        ? prev.map((x) => (x.type === data.type ? data : x))
        : [...prev, data];
    });
  }, []);

  useEffect(() => {
    if (!socket) return;

    socket.on(QuerySocketEvent.QueryChainOfThought, updateSteps);

    return () => {
      socket.off(QuerySocketEvent.QueryChainOfThought, updateSteps);
    };
  }, [socket, updateSteps]);

  return steps;
};

export const ChainOfThoughtSteps = (props: { steps: WS.QueryChainOfThought.Data[] }) => {
  const styles = usePlaceholderStyles();

  if (!props.steps.length) {
    return null;
  }

  return (
    <div>
      {props.steps.map((x) => (
        <ChainOfThoughtItem key={x.type}>
          <StepStatusDescription>
            {x.status === 'in-progress' && <InProgressIcon fontSize="small" />}
            {x.status === 'finished' && <FinishedIcon fontSize="small" />}
            <VerticalLine />
          </StepStatusDescription>
          <StepTextDescription>
            <div>{x.text}</div>
          </StepTextDescription>
        </ChainOfThoughtItem>
      ))}
      <ChainOfThoughtItem>
        <QueuedIcon fontSize="small" />
        <QueuedStepTextDescription>
          <Skeleton css={styles.skeletonItem} variant="text" width={`80%`} />
        </QueuedStepTextDescription>
      </ChainOfThoughtItem>
    </div>
  );
};

export const SystemUserChainOfThoughtMessage = (props: Props) => {
  const steps = useChainOfThoughtSteps();

  const isTakingLong = props.status === ChatQueryStatus.LoadingExtended;

  const device = isMobile();
  const styles = usePlaceholderStyles();

  if (!steps.length) {
    return <SystemUserPlaceholderMessage isTakingLong={isTakingLong} />;
  }

  const { showAuthor } = useAvatarPanel();

  return (
    <div css={styles.root}>
      {showAuthor && !device.phone && (
        <div css={styles.authorWrap}>
          <SystemAuthor animate={true} css={styles.author} />
        </div>
      )}
      <ChainOfThoughtSteps steps={steps} />
    </div>
  );
};

SystemUserChainOfThoughtMessage.displayName = 'Message.System.ChainOfThought';

const InProgressIcon = styled(CircleIcon)(({ theme }) => ({
  color: theme.palette.btnPrimary.bg,
}));

const QueuedIcon = styled(RadioButtonUncheckedIcon)(({ theme }) => ({
  color: theme.palette.btnPrimary.bg,
}));

const FinishedIcon = styled(CheckCircleIcon)(({ theme }) => ({
  color: theme.palette.btnPrimary.bg,
}));

const ChainOfThoughtItem = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
}));

const StepTextDescription = styled.div(({ theme }) => ({
  width: '100%',
  padding: '10px 0',
  borderBottom: `1px solid ${theme.palette.gray.light1}`,
}));

const QueuedStepTextDescription = styled.div(({ theme }) => ({
  width: '100%',
  padding: '10px 0',
}));

const StepStatusDescription = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
}));

const VerticalLine = styled.div(({ theme }) => ({
  position: 'absolute',
  top: 20,
  left: 9,
  width: 2,
  height: 20,
  backgroundColor: theme.palette.btnPrimary.bg,
}));
